/**
 * The public lobby: the live room list, the online count and the lobby chat.
 *
 * Filtering is a store concern rather than a query the server answers, because
 * `LobbyRoom` pushes the whole public list continuously (backend.md §3.8) and
 * re-filtering an array of tens of rooms is free next to a round trip.
 */

import type { StateCreator } from 'zustand';

import type { LobbyChatMessage } from '@ordago/shared';

import type { LobbyFilterState, LobbySlice, Slices } from './types';

const MAX_CHAT = 100;

export const DEFAULT_LOBBY_FILTERS: LobbyFilterState = {
  variant: 'any',
  targetPoints: 'any',
  paceTier: 'any',
  senasMode: 'any',
  hasSpace: false,
  noBots: false,
};

export const createLobbySlice: StateCreator<Slices, [], [], LobbySlice> = (set) => ({
  lobby: {
    connection: 'idle',
    rooms: [],
    onlineCount: 0,
    chat: [],
    filters: DEFAULT_LOBBY_FILTERS,

    setConnection: (connection) => {
      set((s) => ({ lobby: { ...s.lobby, connection } }));
    },

    setRooms: (rooms) => {
      set((s) => ({ lobby: { ...s.lobby, rooms } }));
    },

    setOnlineCount: (onlineCount) => {
      set((s) => ({ lobby: { ...s.lobby, onlineCount } }));
    },

    pushChat: (msg) => {
      set((s) => ({ lobby: { ...s.lobby, chat: appendCapped(s.lobby.chat, msg) } }));
    },

    setFilters: (patch) => {
      set((s) => ({ lobby: { ...s.lobby, filters: { ...s.lobby.filters, ...patch } } }));
    },

    // Filters deliberately survive: a player who filtered for `4r` before
    // opening a room expects the same list when they come back to the lobby.
    reset: () => {
      set((s) => ({
        lobby: { ...s.lobby, connection: 'idle', rooms: [], onlineCount: 0, chat: [] },
      }));
    },
  },
});

function appendCapped(list: LobbyChatMessage[], msg: LobbyChatMessage): LobbyChatMessage[] {
  const next = [...list, msg];
  return next.length > MAX_CHAT ? next.slice(next.length - MAX_CHAT) : next;
}

/** Pure, so the lobby list and its "n rooms" count can never disagree. */
export function matchesFilters(
  room: {
    variant: string;
    targetPoints: number;
    paceTier: string;
    senasMode: string;
    occupancy: number;
    maxClients: number;
    hasBots: boolean;
  },
  f: LobbyFilterState,
): boolean {
  if (f.variant !== 'any' && room.variant !== f.variant) return false;
  if (f.targetPoints !== 'any' && room.targetPoints !== f.targetPoints) return false;
  if (f.paceTier !== 'any' && room.paceTier !== f.paceTier) return false;
  if (f.senasMode !== 'any' && room.senasMode !== f.senasMode) return false;
  if (f.hasSpace && room.occupancy >= room.maxClients) return false;
  if (f.noBots && room.hasBots) return false;
  return true;
}
